import { CheckCircle2, XCircle } from 'lucide-react';
import * as React from 'react';

import type { CguAcceptanceResult } from '@/api/legal-client';
import { useAcceptCgu, useCurrentCgu, useCurrentCguChangelog, useHasSignedCgu } from '@/api/use-legal';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { Label } from '@/components/ui/label';
import { CguPdfDownloadButton } from '@/routes/cgu/cgu-pdf-download';
import { CguResignatureBanner } from '@/routes/cgu/cgu-resignature-banner';
import { DocumentViewer } from '@/routes/cgu/document-viewer';

// =============================================================================
// cgu-screen.tsx — Écran F-02 « Onboarding + CGU » (UC-F-002, EF-LEG-3).
// =============================================================================
// Affichage du document CGU via DocumentViewer (abstraction agnostique du
// renderer) → SCROLL-GATE : le viewer ÉMET `onFullyScrolled` quand l'INTÉGRALITÉ
// du document a été parcourue → débloque le bouton « Signer » (intentionnalité par
// lecture, UC-F-002 E1) → MODALE de confirmation (pattern T-03) → signature (API,
// immuable, DE.CguAcceptanceRecorded legally-binding) → redirection grille.
//
// Le flux d'acceptation gate UNIQUEMENT sur `onFullyScrolled` : il ne connaît rien
// du renderer interne (S5 = placeholder zéro-dep ; S7 = pdf.js drop-in, même
// abstraction, flux inchangé). `contentDisplayed=true` n'est posé QU'APRÈS scroll
// complet ET acceptation explicite — preuve légale (sha256 [cguVersionId→pdfSha256]
// + scroll complet [tout vu] + acceptation explicite). L'UI ne décide pas de la
// valeur juridique : elle garantit l'intention et relaie au serveur (autorité).
//
// a11y (WCAG 2.1 AA) : <h1> unique ; zone de lecture focusable au clavier (dans le
// viewer) ; bouton désactivé tant que non parcouru (état doublé d'un libellé texte,
// jamais couleur seule) ; modale role="dialog" aria-modal ; statut/erreur annoncés.
//
// État : in-memory (React + TanStack Query), aucun localStorage (RULE_REF_006).

export interface CguScreenProps {
  /**
   * Appelé après signature réussie : l'appelant route vers la grille. Reçoit le
   * résultat (acceptanceId) pour traçabilité éventuelle.
   */
  readonly onSigned: (result: CguAcceptanceResult) => void;
}

export function CguScreen({ onSigned }: CguScreenProps): React.JSX.Element {
  const cgu = useCurrentCgu();
  const accept = useAcceptCgu();
  // T-06 (DBU §3.1) : distingue une RESIGNATURE (une version antérieure a déjà été signée) d'un
  // onboarding initial. Autorité = API (GET has-signed) ; on n'affiche le bandeau que sur resignature.
  const signStatus = useHasSignedCgu();
  // DBU §3.1 : résumé des évolutions à afficher dans le bandeau de resignature (texte brut, rendu
  // échappé par le bandeau). Consommé uniquement sur resignature ; undefined tant que non chargé.
  const changelog = useCurrentCguChangelog();
  const isResignature =
    signStatus.data?.hasSigned === false &&
    signStatus.data.signedVersionId !== undefined;
  // DBU §3.1 / lien footer « CG » : un utilisateur ayant DÉJÀ signé la version courante ouvre /cgu pour
  // RELIRE les conditions qu'il a acceptées (accès à l'outil), pas pour re-signer. On bascule alors le
  // bloc de signature (case + bouton) en état LECTURE SEULE (bandeau « déjà accepté ») ; le document et
  // le téléchargement PDF restent disponibles.
  const alreadySigned = signStatus.data?.hasSigned === true;
  // Signal du scroll-gate : passé à `true` UNE FOIS, quand le DocumentViewer a émis
  // `onFullyScrolled`. Le flux d'acceptation ne dépend QUE de cet état.
  const [hasReadToEnd, setHasReadToEnd] = React.useState(false);
  // Case d'engagement (DBU §3.1, l.2402/2406) : consentement explicite EN PLUS du
  // scroll-gate. La signature est bloquée tant que la case n'est pas cochée. La case
  // elle-même n'est activable qu'après lecture complète (DBU l.2404 : intentionnalité
  // par lecture intégrale, symétrique au mécanisme de publication Admin).
  const [engagementChecked, setEngagementChecked] = React.useState(false);
  const [confirmOpen, setConfirmOpen] = React.useState(false);

  const onFullyScrolled = React.useCallback((): void => {
    setHasReadToEnd(true);
  }, []);

  // Blocage cumulatif : lecture complète (scroll-gate existant) ET case d'engagement
  // cochée. Aucune des deux conditions ne suffit seule (DBU §3.1).
  const canSign = hasReadToEnd && engagementChecked;

  const cguVersionId = cgu.data?.cguVersionId ?? '';
  const versionNumber = cgu.data?.versionNumber ?? '';

  const onConfirmSign = (): void => {
    setConfirmOpen(false);
    // `contentDisplayed=true` posé ICI : scroll complet (hasReadToEnd, garanti par le
    // bouton désactivé sinon) + acceptation explicite (confirmation). Preuve légale.
    accept.mutate(
      { cguVersionId, contentDisplayed: true },
      {
        onSuccess: (result) => {
          onSigned(result);
        },
      },
    );
  };

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <header className="space-y-1">
        <h1 className="text-2xl font-semibold tracking-tight">
          Conditions générales d'utilisation
        </h1>
        <p className="text-muted-foreground">
          Veuillez lire l'intégralité des conditions ci-dessous. Le bouton de signature se débloque
          une fois la lecture terminée.
        </p>
      </header>

      {/* DBU §3.1 : bandeau contextuel de RESIGNATURE (une version antérieure a déjà été signée). */}
      {isResignature && cgu.isSuccess ? (
        <CguResignatureBanner
          versionNumber={cgu.data.versionNumber}
          changelog={changelog.data?.changelog}
        />
      ) : null}

      {cgu.isPending ? (
        <p role="status" className="text-muted-foreground">
          Chargement des conditions générales…
        </p>
      ) : null}

      {cgu.isError ? (
        <p role="alert" className="text-destructive">
          Erreur de chargement des CGU : {cgu.error.message}
        </p>
      ) : null}

      {cgu.isSuccess ? (
        <Card aria-labelledby="cgu-version-title">
          <CardHeader>
            <CardTitle id="cgu-version-title" className="text-lg">
              Version {cgu.data.versionNumber}
            </CardTitle>
            <CardDescription>
              Conditions applicables à compter de leur publication. Lecture intégrale requise avant
              signature.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <DocumentViewer
              pdfRef={cgu.data.pdfRef}
              label="Texte des conditions générales d'utilisation"
              onFullyScrolled={onFullyScrolled}
            />

            {/* DBU §3.1 : téléchargement du PDF horodaté (archivage personnel) — toujours disponible. */}
            <CguPdfDownloadButton versionNumber={cgu.data.versionNumber} />

            {alreadySigned ? (
              <div
                role="status"
                className="flex items-start gap-3 rounded-md border border-success/40 bg-success/10 p-4"
              >
                <CheckCircle2
                  className="mt-0.5 h-5 w-5 shrink-0 text-success"
                  aria-hidden="true"
                />
                <p className="text-sm font-medium text-success">
                  Vous avez déjà accepté la version {cgu.data.versionNumber} des conditions
                  générales. Vous pouvez les relire ci-dessus ou télécharger le PDF.
                </p>
              </div>
            ) : (
              <>
                <div aria-live="polite" aria-atomic="true">
              {accept.isError ? (
                <div
                  role="alert"
                  className="flex items-start gap-3 rounded-md border border-destructive/40 bg-destructive/10 p-4"
                >
                  <XCircle
                    className="mt-0.5 h-5 w-5 shrink-0 text-destructive"
                    aria-hidden="true"
                  />
                  <div className="space-y-1">
                    <p className="font-medium text-destructive">Signature non enregistrée</p>
                    <p className="text-sm text-foreground">{accept.error.message}</p>
                  </div>
                </div>
              ) : null}

              {accept.isSuccess ? (
                <div
                  role="status"
                  className="flex items-start gap-3 rounded-md border border-success/40 bg-success/10 p-4"
                >
                  <CheckCircle2
                    className="mt-0.5 h-5 w-5 shrink-0 text-success"
                    aria-hidden="true"
                  />
                  <p className="text-sm font-medium text-success">
                    Conditions signées. Redirection en cours…
                  </p>
                </div>
              ) : null}
            </div>

            {/* DBU §3.1 (l.2402) : case d'engagement obligatoire, mention EXACTE rendue
                verbatim. Activable UNIQUEMENT après lecture complète (l.2404). a11y :
                input natif associé au libellé via htmlFor/id (WCAG 1.3.1 / 4.1.2). */}
            <div className="flex items-start gap-3">
              <input
                id="cgu-engagement"
                type="checkbox"
                checked={engagementChecked}
                disabled={!hasReadToEnd}
                onChange={(event) => setEngagementChecked(event.target.checked)}
                aria-describedby={!hasReadToEnd ? 'cgu-engagement-hint' : undefined}
                className="mt-0.5 h-4 w-4 shrink-0 rounded border border-input accent-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
              />
              <Label htmlFor="cgu-engagement" className="leading-snug">
                J'ai lu et j'accepte les conditions générales d'utilisation de la plateforme JeKo.
              </Label>
            </div>
            {!hasReadToEnd ? (
              <p id="cgu-engagement-hint" className="text-sm text-muted-foreground">
                La case d'engagement s'activera après la lecture complète des conditions.
              </p>
            ) : null}

            <Button
              type="button"
              onClick={() => setConfirmOpen(true)}
              disabled={!canSign || accept.isPending || accept.isSuccess}
              aria-describedby={!canSign ? 'cgu-sign-hint' : undefined}
            >
              {accept.isPending ? 'Signature en cours…' : 'Accepter et continuer'}
            </Button>
            {!canSign ? (
              <p id="cgu-sign-hint" className="sr-only">
                {!hasReadToEnd
                  ? 'Le bouton sera activé après la lecture complète des conditions puis le cochage de la case d’engagement.'
                  : 'Cochez la case d’engagement pour activer le bouton.'}
              </p>
            ) : null}
              </>
            )}
          </CardContent>
        </Card>
      ) : null}

      <ConfirmDialog
        open={confirmOpen}
        title="Confirmer la signature"
        description={`Vous vous apprêtez à signer les conditions générales ${versionNumber}. Cette signature a une valeur juridique et est conservée de manière immuable.`}
        confirmLabel="Confirmer et signer"
        confirmDisabled={accept.isPending}
        onConfirm={onConfirmSign}
        onCancel={() => setConfirmOpen(false)}
      />
    </div>
  );
}
